// Essential
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

import { LoadDiaryList, LoadFarm } from "../../reduxaction/post";
import { DiaryClasstype, TotaldataType, TodalExcelType, } from "../../reducer/postpayloadtypes";
import { CSVLink, CSVDownload } from "react-csv";

// reducer
import { RootState } from "../../reducer/index";
import userSlice from "../../reducer/user";

// library
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { DataGrid, GridColDef, } from '@mui/x-data-grid'; // mui datagrid

// style
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import styled from "styled-components/macro";
import { DataGridStyle } from "../../styles/DataGridStyle";

// 환경종합데이터 페이지 import
import ExcelDataCrossCon from "./ExcelDataCrossCon";

//  미사용
import InsectName from "./PostDiary/component/InsectName";
import InsectLifeCycle from "./PostDiary/component/InsectLifeCycle";
import { SelectOptionType } from "../../FrontType";
import postSlice from "../../reducer/post";

dayjs.locale("ko");


// type 선언
type ComprehensiveDataType = {
	results: {
		headerColumns: { key: string, header: string }[],
		data: any[],
	},
	total: number;
};

const ExcelData = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	//! 데이터를 관리하기 위한 state 선언
	// 농장 정보를 가진 localStorage를 가져옴
	const targetFarmInfo = JSON.parse(localStorage.getItem("targetfarmInfo") || "{}");
	const targetFarmInfoMemo = useMemo(() => targetFarmInfo, [JSON.stringify(targetFarmInfo)]);

	// 선택된 selectTagList 정보를 담는 state
	const [tagList, setTagList] = useState<any[]>([]);

	// 선택된 수집주기, 기간 정보를 담는 state
	const [selectOffset, setSelectOffset] = useState<number>(1);
	const [selectStartDate, setSelectStartDate] = useState<string>(dayjs().subtract(1, "month").format("YYYY-MM-DD"));
	const [selectEndDate, setSelectEndDate] = useState<string>(dayjs().format("YYYY-MM-DD"));

	// 환경 종합데이터 정보를 담는 state
	const [comprehensiveData, setComprehensiveData] = useState<ComprehensiveDataType | null>(null);

	// Pagination Model
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

	// 데이터 그리드의 컬럼, 행 정의
	const [columns, setColumns] = useState<GridColDef[]>([]);
	const [rows, setRows] = useState<any[]>([]);
	const [backdropState, setBackdropState] = useState<boolean>(false);

	//! API
	const getComprehensive = useCallback(async () => {
		try {
			if (tagList.length === 0) return;

			const response = await axios.get(`/ifactory/api/sensorrecord/comprehensive`, {
				params: {
					startDate: selectStartDate, // 검색 시작 날짜
					endDate: selectEndDate, // 검색 종료 날짜
					offset: selectOffset, // 수집주기
					tagIds: tagList.map((tag) => tag.tagId).join(','), // 선택된 태그 리스트
					page: paginationModel.page, // 현재 페이지
					size: paginationModel.pageSize, // 페이지 사이즈
				},
			});
			return response.data;
		} catch (error: any) {
			console.error(error);
		}
	}, [tagList, selectStartDate, selectEndDate, selectOffset, paginationModel]);


	//! React Hook (mount 시 실행)

	//! fn
	// 검색 버튼 클릭 시 실행
	const handleSearch = async () => {
		// validation 체크
		const validation = () => {
			if (tagList.length === 0) {
				confirmAlert({ title: "태그를 선택해주세요", buttons: [{ label: "확인" }] });
				return false;
			}
			if (selectOffset < 1 || selectOffset > 60) {
				confirmAlert({ title: "수집주기를 1~60분 사이로 입력해주세요", buttons: [{ label: "확인" }] });
				return false;
			}
			if (dayjs(selectStartDate).isAfter(dayjs(selectEndDate))) {
				confirmAlert({ title: "기간을 확인해주세요", buttons: [{ label: "확인" }] });
				return false;
			}
			return true;
		};
		if (!validation()) return;

		const comprehensiveData = await getComprehensive();
		setComprehensiveData(comprehensiveData);
	};

	const handleDownload = () => {
		// 파일 다운로드
		const fileName = `환경종합데이터_${dayjs().format("YYYYMMDD_HHmmss")}`;

		const getFile = async () => {
			try {
				const response = await axios.get(`/ifactory/api/sensorrecord/comprehensive/excel`, {
					params: {
						startDate: selectStartDate, // 검색 시작 날짜
						endDate: selectEndDate, // 검색 종료 날짜
						offset: selectOffset, // 수집주기
						tagIds: tagList.map((tag) => tag.tagId).join(','), // 선택된 태그 리스트
					},
					responseType: "blob",
				});
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", `${fileName}.xlsx`);
				document.body.appendChild(link);
				link.click();
			} catch (error: any) {
				console.error(error);
			}

			setBackdropState(false);
		};

		setBackdropState(true);
		getFile();
	};


	//! useEffect
	// 데이터 그리드의 컬럼 정의
	useEffect(() => {
		const createColumns = () => {
			const headerColumns = comprehensiveData?.results.headerColumns || [];
			return headerColumns.map(column => ({
				field: column.key,
				headerName: column.header,
				sortable: true,
				width: 200,
				headerAlign: 'center' as 'center',
				align: 'center' as 'center',
			}));
		};

		const columns = createColumns();
		// rowNum가 필요할 경우
		// columns.unshift({
		// 	field: 'id',
		// 	headerName: 'No',
		// 	width: 100,
		// 	headerAlign: 'center' as 'center',
		// 	align: 'center' as 'center',
		// 	sortable: false,
		// });
		setColumns(columns);
	}, [comprehensiveData]);

	useEffect(() => {
		const rows = comprehensiveData?.results.data.map((row, index) => ({
			id: index + 1,
			...row,
			time: dayjs(row.time).format('YYYY-MM-DD A HH:mm'), // time 필드를 가진 데이터를 변환
		}));
		setRows(rows || []);

	}, [comprehensiveData, columns]);

	// 페이지 변경 처리
	useEffect(() => {
		const handlePagination = async () => {
			const comprehensiveData = await getComprehensive();
			setComprehensiveData(comprehensiveData);
		};
		handlePagination();
	}, [paginationModel]);


	return (
		<Container>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={backdropState}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			<PageTop>
				<p className="pagetit">환경 종합데이터</p>
				<p className="move-btn">
					<img
						src="./img/X.svg"
						onClick={() => {
							navigate(-1);
						}}
						alt="X"
					/>
				</p>
			</PageTop>

			{/* 장소 및 태그 선택 */}
			<ExcelDataCrossCon setTagList={setTagList} />

			{/* 수집주기 및 기간 선택  */}
			<SearchOptionBox>
				{/* 수집주기 입력창(0~60)분 */}
				<div className="diary-select-item">
					<p className="title">수집주기(분) <span>※ 1~60분</span></p>
					<input
						type="number"
						value={selectOffset}
						onChange={(e) => setSelectOffset(Number(e.target.value))}
						min={1}
						max={60}
					/>
				</div>

				{/* 기간 선택 */}
				<div className="diary-select-item">
					<p className="title">기간 선택</p>
					<input
						type="date"
						value={selectStartDate}
						onChange={(e) => setSelectStartDate(e.target.value)}
					/>
					<span style={{ margin: "0 5px" }}> ~ </span>
					<input
						type="date"
						value={selectEndDate}
						onChange={(e) => setSelectEndDate(e.target.value)}
					/>
				</div>

				{/* 검색 버튼	 */}
				<div className="diary-select-btn">
					<button type="button" onClick={handleSearch} >
						검색
					</button>
				</div>
			</SearchOptionBox>


			<div style={{ borderBottom: "1px solid #dddddd" }} />
			{/* 환경 종합데이터 DATA GRID */}

			<DataGridStyle>
				<style>
					{`
     				 .MuiDataGrid-cell, .MuiDataGrid-columnHeader{
        				box-shadow:  0.5px 0 0.5px 0 #dddddd;
      				  }
    				`}
				</style>
				<div className="DataGridWrap">
					{columns && columns.length > 0 && rows && rows.length > 0 && comprehensiveData ? (
						<div>
							{/* 파일 다운로드를 위한 버튼 생성 */}
							<button className="download_btn" onClick={handleDownload}>내보내기</button>
							<DataGrid
								columns={columns}
								rows={rows}

								initialState={{
									pagination: {
										paginationModel: { pageSize: 50, page: 0 },
									},
								}}

								paginationMode="server"
								onPaginationModelChange={setPaginationModel}
								rowCount={comprehensiveData.total}
								pageSizeOptions={[50]}
								disableColumnMenu
							/>
						</div>

					) : (
						<div className="noData">
							<p>조회된 데이터가 없습니다.</p>
						</div>
					)}
				</div>
			</DataGridStyle>


		</Container>
	);
};

export default ExcelData;


const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
`;

const PageTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #dddddd;
  background-color: #fff;
  padding: 15px 0;
  font-size: 1.13em;
  position: relative;
  .pagetit {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 2.5vw;
    font-family: "spoqa500";
  }
  .move-btn {
    position: absolute;
    height: 100%;
    top: 0;
    right: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    & img {
      cursor: pointer;
      width: 100%;
    }
  }

  @media screen and (min-width: 1050px) {
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #dddddd;
    background-color: #fff;
    padding: 20px 0;
    font-size: 1.4em;
    position: relative;

    .move-btn {
      position: absolute;
      width: 1.5%;
      height: 100%;
      top: 0;
      right: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      & img {
        cursor: pointer;
        width: 100%;
      }
    }
  }
`;

const SearchOptionBox = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	padding: 0 40px;
	margin-bottom: 10px;
	
	.diary-select-item{
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		margin: 0 20px;
		white-space: nowrap;  // 줄바꿈 방지
		.title{
			font-size: 0.9em;
			margin-right: 10px;
			span{
				font-size: 0.7em;
				color: #979797;
			}
		}
		input{
			width: 100%;
			height: 40px;
			border: 1px solid #ddd;
			border-radius: 8px;
			padding: 0 10px;
			font-size: 0.9em;
			&:focus{
				outline: none;
			}
		}
	}
	.diary-select-btn{
		width: auto;
		margin: 0 20px;
		& button{
			font-size: 1em;
			font-weight: 500;
			background-color: #73972c;
			color: white;
			padding: 8px 28px;
			border-radius: 5px;
			border: 0;
			&:hover{
				cursor: pointer;
			}
		}
	}
`; 
